import { HostAuthBadge, HostDeniedNotice } from "../components/host-helpers.jsx";
import { Button, CardScreen, Form, FormField, FormSubmit, Icon, useForm } from "@thekrystalship/krystal";
import { addConnection, connectHost, registryEntry, setAppUser } from "../lib/connect.js";

// HostAccess.jsx — UI for the per-host identity/session layer (Model A).
//
//   HostAuthBadge   — per-host session pill (connected / connecting / expired /
//                     no-access). Used in the host switcher, Fleet, Settings.
//   HostDeniedNotice— the TERMINAL 403 surface: identity verified, but nothing
//                     is granted on this host. Never offers re-auth (a 403
//                     re-bounce would loop); only "switch away" / "manage".
//   AddHostPage     — the intermediate "add a host" surface. Shown after login
//                     when no host is configured (the original empty-dashboard
//                     fix) and for adding another host later. Drives the silent
//                     Discord bounce (popup, gesture-bound) and lands a session.

// Honest copy per failure (real connect). needs_auth is NOT a failure: it registers the host and
// reloads into AuthGate's sign-in.
const FAIL = {
  not_kgsm: "That doesn’t look like a kgsm-api. We reached the address but it didn’t answer with a kgsm-api handshake. Double-check the host and port.",
  unreachable: "Couldn’t reach that host. No kgsm-api answered at that address. Check it’s running and reachable from here (host, port, https vs http).",
};

const normalize = (url) => url.trim().replace(/^https?:\/\//, "").replace(/\/+$/, "");
const isHostAddress = (n) =>
  /^[a-z0-9.-]+\.[a-z]{2,}(:\d+)?$/i.test(n) ||
  /^localhost(:\d+)?$/i.test(n) ||
  /^(\d{1,3}\.){3}\d{1,3}(:\d+)?$/.test(n);

function AddHostPage({ user, firstRun, onAdded, onCancel, onLogout }) {
  const form = useForm({
    defaultValues: { url: "" },
    mode: "change",
    validate: { url: (v) => (isHostAddress(normalize(v)) ? undefined : "Enter a valid host address.") },
    // Real connect: probe the public handshake, resolve identity, register + reload.
    //   ok         → auth-disabled / already-authed: identity from /me, into the app.
    //   needs_auth → auth-ENABLED: register the host (URL only, id resolved after
    //                login) and reload → a connected host + no identity → the Discord
    //                AuthGate's sign-in, which bounces against THIS host's /auth/{provider}/start.
    onSubmit: async ({ url }) => {
      const res = await connectHost(url);
      if (res.status === "ok") {
        addConnection(registryEntry(res.origin, res.name, res.hostId));
        setAppUser(res.user);                 // app-shell identity from /me
        window.location.reload();             // re-eval config → boot against the connected host
        return new Promise(() => {});         // the reload ends this page; stay busy until it does
      }
      if (res.status === "needs_auth") {
        // id is null here — /hosts is 401 pre-login; completeOAuthLogin reconciles it
        // with the bearer after Discord. Register so a reload boots connected → the sign-in.
        addConnection(registryEntry(res.origin, res.name, null));
        window.location.reload();
        return new Promise(() => {});
      }
      return { message: FAIL[res.status] };   // not_kgsm | unreachable
    },
  });
  const valid = isHostAddress(normalize(form.values.url));

  return (
    <CardScreen
      mark={import.meta.env.BASE_URL + "assets/tks-mark.png"}
      wordmark="The Krystal Ship"
      tagline={firstRun ? "First, connect to a kgsm-api host." : "Connect another host."}
      foot={(
        <div className="add-host__foot">
          {onCancel
            ? <Button variant="link" size="sm" icon="arrow-left" onClick={onCancel}>Back</Button>
            : <span className="add-host__foot-hint">{firstRun ? "Your hosts are remembered on this device." : "You can add more hosts later."}</span>}
          {onLogout && <Button variant="link" size="sm" icon="log-out" onClick={onLogout}>Sign out</Button>}
        </div>
      )}>
      <div className="auth-card__heading">{firstRun ? "Connect your first host" : "Connect a host"}</div>
      <div className="auth-card__sub">
        A host runs the kgsm-api control panel. Enter its address — we’ll verify it’s a kgsm-api and connect.
      </div>

      <Form form={form}>
        <FormField name="url" label="Host address" mono
          placeholder="http://127.0.0.1:8080  ·  https://your-host"
          spellCheck="false" autoCapitalize="off" autoCorrect="off" />
        <FormSubmit size="xl" wide className="add-host__connect" icon="plug" disabled={!valid}
          busyLabel="Verifying the host…">
          Connect
        </FormSubmit>
      </Form>

      <div className="add-host__note">
        <Icon name="shield-check" size={13} />
        <span>Each host issues its own session and checks your role independently — access can differ per host.</span>
      </div>
    </CardScreen>
  );
}

export { AddHostPage, HostAuthBadge, HostDeniedNotice };
